import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchParams, searchIndex, splitKeywords } from '../../utils/indexer';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { Voter, VoterGroup } from '../../types';
import { useNavigate, useParams } from 'react-router-dom';

import { GoogleGenerativeAI } from "@google/generative-ai";
import VoterGroupListControl from './VoterGroupListControl';
import VoterListControl from '../Voters/VoterListControl';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const groupLevels: VoterGroup['level'][] = [
  "independent", 
  "family", 
  "purok", 
  "barangay", 
  "municipal", 
  "district"
];

const VoterGroupForm: React.FC = () => {
  const { groupId } = useParams<{ groupId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Google Gemini API setup
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY ?? 'AIzaSyBmmS-_40L0zrMqQe0jq5vaojr-VGUp6vU');

  const [voterGroup, setVoterGroup] = useState<VoterGroup>({
    id: '',
    name: '',
    level: 'independent',
    leader: { id: '', name: '' },
    members: [],
    subGroups: [],
    updatedAt: Timestamp.now(),
    updatedBy: user?.display_name ?? 'anonymous',
    purok: '',
    barangay: '',
    city_municipality: '',
    province: ''
  });

  const [openLeaderDialog, setOpenLeaderDialog] = useState(false);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [openSubGroupsDialog, setOpenSubGroupsDialog] = useState(false);
  const [openMemberTransferDialog, setOpenMemberTransferDialog] = useState(false);
  const [memberToTransfer, setMemberToTransfer] = useState<Voter | null>(null);
  const [existingGroupDetails, setExistingGroupDetails] = useState<{id: string, name: string} | null>(null);

  // New state variables for scanning
  const [openScanDialog, setOpenScanDialog] = useState(false);
  const [openUnmatchedNamesDialog, setOpenUnmatchedNamesDialog] = useState(false);
  const [unmatchedNames, setUnmatchedNames] = useState<string[]>([]);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedNames, setProcessedNames] = useState<{total: number, processed: number}>({
    total: 0,
    processed: 0
  });
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  // New states for camera device switching
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraDeviceId, setCurrentCameraDeviceId] = useState<string | null>(null);

  const [errors, setErrors] = useState<{
    name?: string;
    leaderName?: string;
    leaderIdentifier?: string;
  }>({});

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (groupId) {
      // Fetch existing voter group for editing
      const fetchVoterGroup = async () => {
        try {
          const docRef = doc(db, 'voterGroups', groupId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data() as VoterGroup;
            setVoterGroup({
              ...data,
              purok: data.purok ?? '',
              barangay: data.barangay ?? '',
              city_municipality: data.city_municipality ?? '',
              province: data.province ?? ''
            });
          }
        } catch (error) {
          console.error('Error fetching voter group:', error);
          setSubmitError('Failed to fetch voter group details');
        }
      };

      fetchVoterGroup();
    }
  }, [groupId]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!voterGroup.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (!voterGroup.leader.name.trim()) {
      newErrors.leaderName = 'Leader name is required';
    }

    if (!voterGroup.leader.id.trim()) {
      newErrors.leaderIdentifier = 'Leader ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVoterGroup(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear specific error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLevelChange = (e: SelectChangeEvent<VoterGroup['level']>) => {
    const newLevel = e.target.value as VoterGroup['level'];
    setVoterGroup(prev => ({
      ...prev,
      level: newLevel,
      // Clear sub-groups for independent and family levels
      ...(newLevel === 'independent' || newLevel === 'family' ? { subGroups: [] } : {})
    }));
  };

  const handleLeaderSelect = async (voter: Voter) => {
    // Fetch the full voter details to ensure we have the most up-to-date location information
    try {
      const voterDocRef = doc(db, 'voters', voter.id);
      const voterDocSnap = await getDoc(voterDocRef);
      
      if (voterDocSnap.exists()) {
        const fullVoterData = voterDocSnap.data() as Voter;
        
        setVoterGroup(prev => {
          let name = prev.name;
          if (name.trim() === '' && (prev.level === 'family' || prev.level === 'independent')) {
            name = `${voter.name} GROUP`;
          }
          return {
            ...prev,
            leader: {
              id: voter.id,
              name: voter.name
            },
            name: name,
            // Pre-fill location fields from the leader
            purok: fullVoterData.purok ?? '',
            barangay: fullVoterData.barangay || '',
            city_municipality: fullVoterData.city_municipality || '',
            province: fullVoterData.province || '',
          }
        });
      } else {
        // Fallback to original voter data if document not found
        setVoterGroup(prev => ({
          ...prev,
          leader: {
            id: voter.id,
            name: voter.name
          },
          name: prev.name.trim() === '' && (prev.level === 'family' || prev.level === 'independent') 
            ? voter.name 
            : prev.name,
          purok: voter.purok ?? '',
          barangay: voter.barangay || '',
          city_municipality: voter.city_municipality || '',
          province: voter.province || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching voter details:', error);
      // Fallback to original voter data in case of error
      setVoterGroup(prev => ({
        ...prev,
        leader: {
          id: voter.id,
          name: voter.name
        },
        name: prev.name.trim() === '' && (prev.level === 'family' || prev.level === 'independent') 
          ? voter.name 
          : prev.name,
        purok: voter.purok ?? '',
        barangay: voter.barangay || '',
        city_municipality: voter.city_municipality || '',
        province: voter.province || '',
      }));
    }

    setOpenLeaderDialog(false);
    // Clear leader-related errors
    setErrors(prev => ({
      ...prev,
      leaderName: undefined,
      leaderIdentifier: undefined,
      name: undefined // Clear name error if it was set due to empty name
    }));
  };

  const checkVoterGroupMembership = async (voter: Voter) => {
    const voterGroupsRef = collection(db, 'voterGroups');
    const q = query(
      voterGroupsRef, 
      where('members', 'array-contains', { id: voter.id, name: voter.name })
    );

    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const existingGroup = querySnapshot.docs[0];
      return {
        id: existingGroup.id,
        name: existingGroup.data().name
      };
    }
    
    return null;
  };

  const handleMemberSelect = async (voter: Voter) => {
    // Check if voter is already a member of another group
    const existingGroup = await checkVoterGroupMembership(voter);

    if (existingGroup && existingGroup.id !== voterGroup.id) {
      setMemberToTransfer(voter);
      setExistingGroupDetails(existingGroup);
      setOpenMemberTransferDialog(true);
      return;
    }

    // Prevent adding duplicate members
    const isAlreadyMember = voterGroup.members.some(m => m.id === voter.id);
    if (!isAlreadyMember) {
      setVoterGroup(prev => ({
        ...prev,
        members: [...prev.members, { id: voter.id, name: voter.name }]
      }));
    }
    setOpenMembersDialog(false);
  };

  const handleMemberTransfer = async () => {
    if (memberToTransfer && existingGroupDetails) {
      // Remove voter from existing group
      const existingGroupRef = doc(db, 'voterGroups', existingGroupDetails.id);
      const existingGroupSnap = await getDoc(existingGroupRef);
      
      if (existingGroupSnap.exists()) {
        const existingGroupData = existingGroupSnap.data() as VoterGroup;
        const updatedMembers = existingGroupData.members.filter(m => m.id !== memberToTransfer.id);
        
        await updateDoc(existingGroupRef, {
          members: updatedMembers,
          updatedAt: Timestamp.now(),
          updatedBy: user?.display_name ?? 'anonymous'
        });
      }

      // Add voter to current group
      setVoterGroup(prev => ({
        ...prev,
        members: [...prev.members, { id: memberToTransfer.id, name: memberToTransfer.name }]
      }));

      // Close dialogs
      setOpenMemberTransferDialog(false);
      setOpenMembersDialog(false);
      setMemberToTransfer(null);
      setExistingGroupDetails(null);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    setVoterGroup(prev => ({
      ...prev,
      members: prev.members.filter(m => m.id !== memberId)
    }));
  };

  const handleSubGroupSelect = (subGroup: VoterGroup) => {
    // Prevent adding sub-groups for independent and family levels
    if (voterGroup.level === 'independent' || voterGroup.level === 'family') {
      return;
    }

    // Prevent adding duplicate sub-groups
    const isAlreadySubGroup = voterGroup.subGroups.some(sg => sg.id === subGroup.id);
    if (!isAlreadySubGroup) {
      setVoterGroup(prev => ({
        ...prev,
        subGroups: [...prev.subGroups, { id: subGroup.id, name: subGroup.name }]
      }));
    }
    setOpenSubGroupsDialog(false);
  };

  const handleRemoveSubGroup = (subGroupId: string) => {
    setVoterGroup(prev => ({
      ...prev,
      subGroups: prev.subGroups.filter(sg => sg.id !== subGroupId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (groupId) {
        // Update existing voter group
        const docRef = doc(db, 'voterGroups', groupId);
        await updateDoc(docRef, {
          ...voterGroup,
          // Ensure no sub-groups for independent and family levels
          ...(voterGroup.level === 'independent' || voterGroup.level === 'family' ? { subGroups: [] } : {}),
          updatedAt: Timestamp.now(),
          updatedBy: user?.display_name ?? 'anonymous',
        });
      } else {
        // Create new voter group
        const groupsRef = collection(db, "voterGroups");
        const newGroupRef = doc(groupsRef);
        await setDoc(newGroupRef, {
          ...voterGroup,
          // Ensure no sub-groups for independent and family levels
          ...(voterGroup.level === 'independent' || voterGroup.level === 'family' ? { subGroups: [] } : {}),
          id: newGroupRef.id,
          updatedAt: Timestamp.now(),
          updatedBy: user?.display_name ?? 'anonymous',
        });
      }
      
      navigate('/voter-groups');
    } catch (error) {
      console.error('Error saving voter group:', error);
      setSubmitError('Failed to save voter group. Please try again.');
      setIsSaving(false);
    }
  };

  // Define stopCamera before it's used
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setIsVideoReady(false);
    }
  }, [cameraStream]);

  const startCamera = useCallback(async (cameraDeviceId: string | null) => {
    try {
      // Enumerate available cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);

      // If no cameras found, throw an error
      if (videoDevices.length === 0) {
        throw new Error('No video devices found');
      }

      // Prioritize previously selected camera, fallback to first device
      const deviceId = cameraDeviceId || videoDevices[0].deviceId;
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { deviceId: { exact: deviceId } } 
      });
      
      setCameraStream(stream);
      setCurrentCameraDeviceId(deviceId);
      setIsVideoReady(false);

      const setupVideoStream = () => {
        const videoElement = videoRef.current;
        
        if (videoElement) {
          videoElement.srcObject = stream;
          videoElement.onloadedmetadata = () => {
            videoElement.play();
            setIsVideoReady(true);
          };
        } else {
          // If ref is not available, retry
          requestAnimationFrame(setupVideoStream);
        }
      };

      setupVideoStream();
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
      setIsVideoReady(false);
    }
  }, []);

  const switchCamera = useCallback(async (deviceId: string) => {
    // Stop current camera stream
    stopCamera();

    // Set new device ID
    setCurrentCameraDeviceId(deviceId);

    // Restart camera with new device
    await startCamera(deviceId);
  }, [stopCamera, startCamera]);

  // Modify useEffect to handle video ref and camera initialization
  useEffect(() => {
    if (openScanDialog && videoRef.current && cameraStream) {
      const setupVideoStream = () => {
        if (videoRef.current) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
            setIsVideoReady(true);
          };
        } else {
          // Retry if ref is not available
          requestAnimationFrame(setupVideoStream);
        }
      };

      setupVideoStream();
    }
  }, [openScanDialog, cameraStream]);

  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current && isVideoReady) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL('image/jpeg');
        processImage(imageDataUrl);
      }
    }
  }, [isVideoReady]);

  const processImage = async (imageDataUrl: string) => {
    setIsProcessing(true);
    setProcessedNames({ total: 0, processed: 0 });
    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });
      const prompt = `Extract names from this image. Identify a group leader and group members. 
      Provide the result in a strict JSON format: 
      {"leader": "Leader Full Name", "members": ["Member1 Full Name", "Member2 Full Name"]}
      
      Rules:
      - Only return full names
      - Maximum of 1 leader
      - If no names are clear, return an empty array
      - Do not make up names if they are not visible`;

      const result = await model.generateContent({
        contents: [{ role: "user", parts: [
          { text: prompt },
          { inlineData: { mimeType: "image/jpeg", data: imageDataUrl.split(',')[1] } }
        ]}]
      });
      
      const extractedText = result.response.text();
      
      // Remove any text outside of JSON brackets
      const jsonMatch = extractedText.match(/\{[^}]+\}/);
      if (!jsonMatch) {
        throw new Error('Could not parse names from image');
      }
      
      const parsedNames = JSON.parse(jsonMatch[0]);
      
      // Set total names to process
      const totalNames = (parsedNames.leader ? 1 : 0) + (parsedNames.members?.length || 0);
      setProcessedNames({ total: totalNames, processed: 0 });

      await findAndAddVoters(parsedNames);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Could not extract names from the image. Please try again.');
    } finally {
      setOpenScanDialog(false);
      setIsProcessing(false);
      stopCamera();
    }
  };

  // Modify the scan dialog opening to ensure camera is properly set up
  const handleOpenScanDialog = () => {
    setOpenScanDialog(true);
    // Use setTimeout to ensure dialog is rendered before starting camera
    setTimeout(() => {
      startCamera(currentCameraDeviceId);
    }, 100);
  };

  const searchVoter = async (keyword: string): Promise<Voter | null> => {
    const keywords = splitKeywords(keyword).sort((a, b) => b.length - a.length);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: 10,
      document: "voter",
      field: "name",
      searchAfter: null
    }
    let result = await searchIndex(searchParams);
    while ((result.indexes.length <= 0) && (keywords.length > 2)) {
      keywords.pop();
      searchParams.keywords = keywords;
      result = await searchIndex(searchParams);
    }
    if (result.indexes.length > 0) {
      const voterDocRef = doc(db, result.indexes[0].documentPath);
      const voterDoc = await getDoc(voterDocRef);
      if (voterDoc.exists()) {
        return voterDoc.data() as Voter;
      }
    }
    return null;
  };

  const findAndAddVoters = async (parsedNames: { leader: string, members: string[] }) => {
    const unmatchedVoters: string[] = [];

    // Find and add leader
    if (parsedNames.leader) {
      const voter = await searchVoter(parsedNames.leader);
      if (voter) {
        handleLeaderSelect(voter);
      } else {
        unmatchedVoters.push(parsedNames.leader);
      }
      setProcessedNames(prev => ({ ...prev, processed: prev.processed + 1 }));
    }

    // Find and add members
    const membersToAdd: Voter[] = [];
    for (const memberName of parsedNames.members || []) {
      const voter = await searchVoter(memberName);
      if (voter) {
        membersToAdd.push(voter);
      } else {
        unmatchedVoters.push(memberName);
      }
      setProcessedNames(prev => ({ ...prev, processed: prev.processed + 1 }));
    }

    // Add matched members
    membersToAdd.forEach(handleMemberSelect);

    // Show unmatched names dialog if any
    if (unmatchedVoters.length > 0) {
      setUnmatchedNames(unmatchedVoters);
      setOpenUnmatchedNamesDialog(true);
    }
  };

  // Modify the Camera Scan Dialog in the return statement
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography component="h1" variant="h5">
              {groupId ? 'Edit Voter Group' : 'New Voter Group'}
            </Typography>
            
            <Button
              variant="contained"
              color="secondary"
              onClick={handleOpenScanDialog}
              style={{ paddingLeft: 20, paddingRight: 20, height: '100%' }}
              disabled={isSaving}
            >
              Scan
            </Button>
          </Box>
        </Grid>
        {submitError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {submitError}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <TextField
              required
              fullWidth
              label="Leader"
              margin="normal"
              value={voterGroup.leader.name}
              InputProps={{
                readOnly: true,
              }}
              error={!!errors.leaderName || !!errors.leaderIdentifier}
              helperText={errors.leaderName ?? errors.leaderIdentifier}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenLeaderDialog(true)}
              style={{ marginLeft: 10, paddingLeft: 20, paddingRight: 20, height: '100%' }}
              disabled={isSaving}
            >
              Select
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}> 
          <TextField
            required
            fullWidth
            label="Group Name"
            name="name"
            value={voterGroup.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth error={false}>
            <InputLabel>Group Level</InputLabel>
            <Select
              name="level"
              value={voterGroup.level}
              label="Group Level"
              onChange={handleLevelChange}
            >
              {groupLevels.map(level => (
                <MenuItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Purok"
            name="purok"
            value={voterGroup.purok}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Barangay"
            name="barangay"
            value={voterGroup.barangay}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City/Municipality"
            name="city_municipality"
            value={voterGroup.city_municipality}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Province"
            name="province"
            value={voterGroup.province}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Group Members ({voterGroup.members.length})
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenMembersDialog(true)}
              // disabled={isSaving || voterGroup.members.length >= 20}
              disabled={isSaving}
            >
              Add Members
            </Button>
          </Box>
          
          {voterGroup.members.length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {voterGroup.members.map((member) => (
                <Chip
                  key={member.id}
                  label={member.name}
                  onDelete={() => handleRemoveMember(member.id)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No members added yet
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Sub Groups</Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenSubGroupsDialog(true)}
              disabled={isSaving || voterGroup.level === 'independent' || voterGroup.level === 'family'}
            >
              Add Sub Groups
            </Button>
          </Box>
          
          {voterGroup.subGroups.length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {voterGroup.subGroups.map((subGroup) => (
                <Chip
                  key={subGroup.id}
                  label={subGroup.name}
                  onDelete={() => handleRemoveSubGroup(subGroup.id)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {voterGroup.level === 'independent' || voterGroup.level === 'family' 
                ? 'Sub-groups not allowed for this group level' 
                : 'No sub-groups added yet'}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'right' }}>
        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={() => navigate('/voter-groups')}
          disabled={isSaving}
          sx={{ width: 100 }}
        >
          Cancel
        </Button>
        &nbsp;
        &nbsp;
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={isSaving}
          sx={{ width: 100, position: 'relative' }}
        >
          {isSaving ? (
            <CircularProgress 
              size={24} 
              sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }} 
            />
          ) : (
            'Save'
          )}
        </Button>
      </Box>

      {/* Camera Scan Dialog */}
    <Dialog 
      open={openScanDialog} 
      onClose={() => {
        setOpenScanDialog(false);
        stopCamera();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Scan Group Details</DialogTitle>
      <DialogContent>
        <Box>
          {isProcessing ? (
            <Box sx={{ width: '100%', textAlign: 'center', mt: 4, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Processing Voter Names
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={processedNames.total > 0 
                  ? (processedNames.processed / processedNames.total) * 100 
                  : 0} 
                sx={{ width: '100%', mt: 2 }} 
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                {processedNames.total > 0 
                  ? `Processed ${processedNames.processed} of ${processedNames.total} names` 
                  : 'Extracting and matching voter names...'}
              </Typography>
            </Box>
          ) : (
            <>
              <video 
                ref={videoRef} 
                style={{ width: '100%', maxHeight: 480 }}
                playsInline
              />
              <canvas 
                ref={canvasRef} 
                style={{ display: 'none' }} 
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Camera</InputLabel>
                <Select
                  value={currentCameraDeviceId ?? availableCameras[0]?.deviceId}
                  label="Select Camera"
                  onChange={(e) => switchCamera(e.target.value)}
                  disabled={availableCameras.length <= 1}
                >
                  {availableCameras.map((camera) => (
                    <MenuItem key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Camera ${availableCameras.findIndex(c => c.deviceId === camera.deviceId) + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={captureImage}
                fullWidth
                sx={{ mt: 2 }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Capture Image'}
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setOpenScanDialog(false);
          stopCamera();
        }}>Cancel</Button>
      </DialogActions>
    </Dialog>

      {/* Unmatched Names Dialog */}
      <Dialog
        open={openUnmatchedNamesDialog}
        onClose={() => setOpenUnmatchedNamesDialog(false)}
      >
        <DialogTitle>Unmatched Names</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following names could not be found in the voter database:
          </DialogContentText>
          <ul>
            {unmatchedNames.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnmatchedNamesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMemberTransferDialog}
        onClose={() => setOpenMemberTransferDialog(false)}
      >
        <DialogTitle>Transfer Group Membership</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${memberToTransfer?.name} is already a member of the group "${existingGroupDetails?.name}". 
            Do you want to move this voter to the current group?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMemberTransferDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleMemberTransfer} 
            color="primary" 
            variant="contained"
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openLeaderDialog}
        onClose={() => setOpenLeaderDialog(false)}
      >
        <DialogTitle>Select Group Leader</DialogTitle>
        <DialogContent>
          <VoterListControl onSelect={handleLeaderSelect} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeaderDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openMembersDialog}
        onClose={() => setOpenMembersDialog(false)}
      >
        <DialogTitle>Select Group Members</DialogTitle>
        <DialogContent>
          <VoterListControl onSelect={handleMemberSelect} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMembersDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openSubGroupsDialog}
        onClose={() => setOpenSubGroupsDialog(false)}
      >
        <DialogTitle>Select Sub Groups</DialogTitle>
        <DialogContent>
          <VoterGroupListControl 
            onSelect={handleSubGroupSelect} 
            excludeGroups={[voterGroup.id, ...voterGroup.subGroups.map(sg => sg.id)]}
            levelFilter={
              // Only allow sub-groups from levels below the current group's level
              voterGroup.level === 'independent' || voterGroup.level === 'family' 
                ? [] 
                : groupLevels.slice(groupLevels.indexOf(voterGroup.level) + 1)
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubGroupsDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoterGroupForm;
